import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { COLOR_MAP } from "@/lib/shifts";

// A small, collapsible colour legend for the Schedule tab. Order matches the
// palette in /lib/shifts.js so the visual and the docs stay in sync.
const LEGEND = [
  { key: "regular",         label: "Regular",         desc: "Assigned working shift" },
  { key: "pending_magenta", label: "PTO Requested",   desc: "Pending — original shift code shown, opens the shift up on the top open-shifts line" },
  { key: "pto_blue",        label: "PTO Approved",    desc: "Approved paid time off" },
  { key: "absent_red",      label: "Absent",          desc: "Marked absent (Sick / FMLA / Kin Care / Unexcused)" },
  { key: "offered_green",   label: "Offer Pending",   desc: "Open-shift offer sent, waiting for pickup approval" },
  { key: "coverage_pink",   label: "Modified",        desc: "Shift hours were edited from the original" },
  { key: "loa_grey",        label: "Leave of Absence" },
  { key: "unavail_purple",  label: "Unavailable" },
  { key: "open_shift",      label: "Open Shift",      desc: "Needs coverage" },
  { key: "ot_red",          label: "OT Warning",      desc: "This shift pushes the employee into overtime" },
];

export default function ScheduleLegend() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white text-xs" data-testid="sched-legend">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full px-3 py-2 flex items-center gap-2 text-left"
        data-testid="sched-legend-toggle"
      >
        <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0">
          {/* Mobile portrait: hide colour chips entirely so the legend collapses
              to a tiny "Legend ▼" pill. Full chips return at ≥ sm width. */}
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold sm:hidden">Legend</span>
          <div className="hidden sm:flex items-center gap-1 flex-wrap flex-1 min-w-0">
            {LEGEND.slice(0, 5).map((l) => {
              const c = COLOR_MAP[l.key];
              return (
                <span key={l.key} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${c.bg} ${c.text} ${c.border} text-[10px] font-semibold`}>
                  <span className={`w-2 h-2 rounded-sm ${c.bg} border ${c.border}`} />
                  {l.label}
                </span>
              );
            })}
            {!open && <span className="text-[10px] text-slate-400">+ {LEGEND.length - 5} more</span>}
          </div>
        </div>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {LEGEND.map((l) => {
            const c = COLOR_MAP[l.key];
            return (
              <div key={l.key} className="flex items-start gap-2" data-testid={`legend-item-${l.key}`}>
                <div className={`w-6 h-6 rounded border ${c.bg} ${c.text} ${c.border} shrink-0 grid place-items-center font-bold text-[10px]`}>
                  {l.key === "regular" ? "1" : l.key === "absent_red" ? "A" : l.key === "pending_magenta" ? "1" : "" }
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 text-[11px]">{l.label}</div>
                  {l.desc && <div className="text-[10px] text-slate-500 leading-tight">{l.desc}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
